import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/corporate", label: "Corporate" },
  { to: "/programmes", label: "Programmes" },
  { to: "/coaching", label: "For Individuals" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const CALENDLY = "https://www.globalwomenascend.com/intro-call";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-gwa-bg/85 backdrop-blur-xl border-b border-gwa-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="gwa-container flex items-center justify-between h-20 gap-6">
        <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2 group shrink-0">
          <span className="font-serif text-2xl tracking-tight text-gwa-primary leading-none">
            Global Women Ascend
          </span>
          <span className="hidden md:inline-block w-1 h-1 rounded-full bg-gwa-accent" />
        </Link>

        <div className="hidden lg:flex items-center gap-10 xl:gap-14">
          <nav className="flex items-center gap-7 xl:gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `gwa-link-underline whitespace-nowrap text-[0.78rem] tracking-[0.18em] uppercase font-medium transition-colors ${
                    isActive ? "text-gwa-accent" : "text-gwa-primary hover:text-gwa-accent"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <Link
            to="/contact"
            data-testid="header-cta-pipeline"
            className="gwa-btn-primary shrink-0"
          >
            Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-gwa-primary p-2 -mr-2"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-x-0 top-20 bg-gwa-bg border-t border-gwa-border transition-all duration-500 overflow-hidden ${
          open ? "max-h-[100vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="gwa-container py-10 flex flex-col gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-serif text-3xl ${isActive ? "text-gwa-accent" : "text-gwa-primary"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-cta-pipeline"
            className="gwa-btn-primary self-start mt-4"
          >
            Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </nav>
      </div>
    </header>
  );
}
